import React from 'react';
import {useFocusTrap} from '@packages/systems/spring-hooks';

import {FocusTrapView} from './FocusTrap.styles';
import {mergeRefs} from '../../utils';
import type {AsProps, HTMLAttributes, SpringTooling} from '../__SharedTypes';

export type FocusTrapProps = {
  /**
   * Traps the keyboard (Tab) focus for focusable elements within FocusTrap.
   */
  shouldTrapFocus?: boolean;
} & AsProps &
  HTMLAttributes &
  SpringTooling;

/**
 * The `FocusTrap` component is used to contain focus within [dialog elements](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog) for improved accessibility.
 *
 * ## Usage
 *
 * ```jsx
 * import React from 'react';
 * import {FocusTrap} from '@packages/systems/spring/FocusTrap';
 *
 * const Example = () => {
 *   return (
 *     <FocusTrap>
 *       <button>Action</button>
 *       <button>Action</button>
 *       <button>Action</button>
 *     </FocusTrap>
 *   );
 * };
 * ```
 *
 * @component
 */
export const FocusTrap = React.forwardRef<any, any>(
  ({shouldTrapFocus = true, ...props}, ref) => {
    const focusRef = useFocusTrap(shouldTrapFocus);
    const refs = mergeRefs([ref, focusRef]);

    return <FocusTrapView tabIndex={-1} {...props} ref={refs} />;
  }
);
